import { api, ApiError } from '../api.js';
import { auth } from '../auth.js';
import { router } from '../router.js';
import { escapeHtml, showToast, setReturnTo, uploadType, enhanceCodeInput } from '../utils.js';

/**
 * The guest page. Most people who see this have no portal account and never will.
 *
 * There is no teardown hook in the router, so every timer this page starts is cleared at
 * the top of its own render -- the same discipline gallery.js follows. Leaking a poll here
 * would keep hitting the API from whatever page the user navigated to next.
 */
let pollTimer = null;
let slideTimer = null;
let recorder = null;

const state = {
    slug: null,
    token: null,
    /** How this visitor is getting in: a confirmed guest code, or their portal session. */
    credential: null,
    event: null,
    media: [],
    guestbook: [],
    view: 'slideshow',
    slide: 0,
};

const tokenKey = (slug) => `v2.event.${slug}.token`;

function readToken(slug) {
    try {
        return localStorage.getItem(tokenKey(slug));
    } catch {
        return null;
    }
}

function writeToken(slug, token) {
    try {
        if (token) localStorage.setItem(tokenKey(slug), token);
        else localStorage.removeItem(tokenKey(slug));
    } catch {
        // Private browsing refuses storage. The page still works for this visit; the guest
        // just has to confirm their email again next time.
    }
}

/**
 * The background is a full-bleed image behind everything, set as a custom property so the
 * stylesheet owns how it is drawn (`cover`, centred, fixed) and this only supplies the URL.
 *
 * It is presigned and expires, so it is re-applied from every feed response rather than set
 * once -- an event page left open on a screen all evening would otherwise lose its
 * background the moment the URL aged out.
 */
function applyBackground(url) {
    // The value goes inside a CSS url("...") string, so anything that could close that
    // string early is refused rather than escaped. A presigned S3 URL is percent-encoded
    // and contains none of these; if one ever did, no background is better than a broken
    // stylesheet.
    const usable = typeof url === 'string' && /^https:\/\//.test(url) && !/["'\\()\s]/.test(url);

    if (usable) {
        document.body.style.setProperty('--event-bg', `url("${url}")`);
        document.body.classList.add('has-event-bg');
    } else {
        document.body.classList.remove('has-event-bg');
        document.body.style.removeProperty('--event-bg');
    }
}

function stopTimers() {
    clearInterval(pollTimer);
    clearInterval(slideTimer);
    pollTimer = null;
    slideTimer = null;
    if (recorder?.state === 'recording') recorder.stop();
    recorder = null;
}

export async function renderEventGuest(params) {
    stopTimers();
    checkGeneration += 1;
    const app = document.getElementById('app');

    state.slug = params?.slug ?? '';
    state.token = readToken(state.slug);
    state.media = [];
    state.guestbook = [];
    state.slide = 0;
    applyBackground(null);

    app.innerHTML = `<div class="event-guest-shell"><p class="text-muted">Loading…</p></div>`;

    // A guest code the visitor already has beats their portal session: someone signed in on
    // a shared phone should stay whoever they joined as, rather than silently switching
    // identity halfway through an evening.
    state.credential = state.token
        ? { eventToken: state.token }
        : auth.isAuthenticated ? { session: true } : null;

    if (state.credential) {
        try {
            await loadFeed();
            return drawFeed();
        } catch (err) {
            // An expired guest token means the event ended or an organiser blocked them;
            // a refused session just means this route needs the guest flow instead. Either
            // way, fall back to the join form rather than a dead end.
            if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
                if (state.token) writeToken(state.slug, null);
                state.token = null;
                state.credential = null;
            } else {
                return drawFatal(err.message);
            }
        }
    }

    try {
        state.event = await api.getPublicEvent(state.slug);
        applyBackground(state.event.background_url);
    } catch (err) {
        return drawFatal(err instanceof ApiError && err.status === 404
            ? "That link doesn't match an event."
            : err.message);
    }
    drawJoin();
}

function drawFatal(message) {
    document.getElementById('app').innerHTML = `
        <div class="event-guest-shell">
            <div class="event-guest-card">
                <h1>Sorry</h1>
                <p>${escapeHtml(message)}</p>
            </div>
        </div>`;
}

function eventWhen(ev) {
    const starts = new Date(ev.starts_at);
    const ends = new Date(ev.ends_at);
    const date = starts.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    const from = starts.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    const to = ends.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    return starts.toDateString() === ends.toDateString() ? `${date}, ${from} – ${to}` : date;
}

// ------------------------------------------------------------------ Joining

function drawJoin() {
    const ev = state.event;
    const notYet = !ev.is_active;
    const finished = notYet && new Date() > new Date(ev.ends_at);

    document.getElementById('app').innerHTML = `
        <div class="event-guest-shell">
            <div class="event-guest-card">
                <h1>${escapeHtml(ev.name)}</h1>
                <p class="event-guest-when">${escapeHtml(eventWhen(ev))}</p>
                ${ev.location ? `<p class="event-guest-where"><i class="fa-solid fa-location-dot"></i> ${escapeHtml(ev.location)}</p>` : ''}
                ${ev.description ? `<p class="event-guest-blurb">${escapeHtml(ev.description)}</p>` : ''}

                ${finished
                    ? '<p class="event-guest-closed">This event has finished. Thank you to everyone who joined in.</p>'
                    : notYet
                        ? '<p class="event-guest-closed">This page opens when the event starts. Do come back then.</p>'
                        : joinFormHtml()}
            </div>
        </div>`;

    if (ev.is_active) bindJoinForm();
}

function joinFormHtml() {
    return `
        <p class="event-guest-intro">
            Add your photos, video and a message to the guest book. Give your name and email
            and we'll send you a six-digit code to type in below.
        </p>
        <form id="join-form" class="event-join-form">
            <div class="form-group">
                <label for="join-name">Your name</label>
                <input type="text" id="join-name" maxlength="120" required autocomplete="name">
            </div>
            <div class="form-group">
                <label for="join-email">Your email</label>
                <input type="email" id="join-email" maxlength="255" required autocomplete="email"
                       inputmode="email" autocapitalize="off">
            </div>
            <div id="join-error" class="form-error hidden"></div>
            <button type="submit" class="btn btn-primary btn-block">Send me a code</button>
            <p class="form-hint">
                Your address is used to confirm it's really you, and to let the organisers
                see who added what. It isn't shared with anyone else.
                If you already have a Shanks Family Portal account, we'll ask you to sign in
                instead &mdash; no code needed.
            </p>
        </form>

        <form id="code-form" class="event-join-form hidden">
            <p class="event-code-sent">
                We've emailed a code to <strong id="code-target"></strong>. It expires in ten minutes.
            </p>
            <div class="form-group">
                <label for="join-code">Six-digit code</label>
                <input type="text" id="join-code" inputmode="numeric" pattern="[0-9]*" maxlength="6"
                       autocomplete="one-time-code" class="event-code-input">
            </div>
            <div id="code-error" class="form-error hidden"></div>
            <button type="submit" class="btn btn-primary btn-block">Join the event</button>
            <button type="button" id="code-back" class="btn btn-secondary btn-block">Use a different address</button>
        </form>`;
}

function bindJoinForm() {
    const joinForm = document.getElementById('join-form');
    const codeForm = document.getElementById('code-form');
    const joinError = document.getElementById('join-error');
    const codeError = document.getElementById('code-error');

    joinForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        joinError.classList.add('hidden');
        const button = joinForm.querySelector('button[type="submit"]');
        const email = document.getElementById('join-email').value.trim();
        button.disabled = true;
        button.textContent = 'Sending…';

        try {
            const res = await api.registerEventGuest(state.slug, {
                name: document.getElementById('join-name').value,
                email,
            });

            // They already have a portal account, so there is nothing to prove by email.
            // Send them to sign in and bring them straight back here afterwards.
            if (res?.portal_account) {
                setReturnTo(`/e/${state.slug}`);
                showToast('That address has a portal account — please sign in', 'info');
                await router.navigate('/login');
                return;
            }

            document.getElementById('code-target').textContent = email;
            joinForm.classList.add('hidden');
            codeForm.classList.remove('hidden');
            document.getElementById('join-code').focus();
        } catch (err) {
            joinError.textContent = err.message;
            joinError.classList.remove('hidden');
        } finally {
            button.disabled = false;
            button.textContent = 'Send me a code';
        }
    });

    enhanceCodeInput(document.getElementById('join-code'));

    codeForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        codeError.classList.add('hidden');
        const button = codeForm.querySelector('button[type="submit"]');
        button.disabled = true;

        try {
            const res = await api.verifyEventGuest(state.slug, {
                email: document.getElementById('join-email').value.trim(),
                code: document.getElementById('join-code').value.trim(),
            });
            state.token = res.token;
            state.credential = { eventToken: res.token };
            writeToken(state.slug, res.token);
            await loadFeed();
            drawFeed();
        } catch (err) {
            codeError.textContent = err.message;
            codeError.classList.remove('hidden');
        } finally {
            button.disabled = false;
        }
    });

    document.getElementById('code-back').addEventListener('click', () => {
        codeForm.classList.add('hidden');
        joinForm.classList.remove('hidden');
    });
}

// ------------------------------------------------------------------ The feed

async function loadFeed() {
    const res = await api.getEventFeed(state.slug, state.credential);
    state.event = res.event;
    state.guest = res.guest;
    applyBackground(res.event.background_url);
    state.media = res.media;
    state.guestbook = res.guestbook;
}

function drawFeed() {
    stopTimers();
    const ev = state.event;

    document.getElementById('app').innerHTML = `
        <div class="event-live">
            <header class="event-live-header">
                <div>
                    <h1>${escapeHtml(ev.name)}</h1>
                    <p>${escapeHtml(eventWhen(ev))}</p>
                </div>
                <div class="event-live-actions">
                    ${ev.allow_uploads ? `
                        <label class="btn btn-primary event-upload-btn">
                            <i class="fa-solid fa-camera"></i> Add photos
                            <input type="file" id="event-file" accept="image/*,video/*,.heic,.heif" multiple hidden>
                        </label>` : ''}
                    ${ev.allow_guestbook ? `
                        <button type="button" id="open-guestbook" class="btn btn-secondary">
                            <i class="fa-solid fa-pen-nib"></i> Sign the guest book
                        </button>` : ''}
                </div>
            </header>

            <div id="upload-progress" class="event-upload-progress hidden"></div>

            <div class="event-view-toggle">
                <button type="button" data-view="slideshow" class="${state.view === 'slideshow' ? 'active' : ''}">
                    <i class="fa-regular fa-square"></i> Slideshow
                </button>
                <button type="button" data-view="grid" class="${state.view === 'grid' ? 'active' : ''}">
                    <i class="fa-solid fa-table-cells"></i> Grid
                </button>
            </div>

            <div id="event-stage"></div>
            <div id="event-ticker"></div>
        </div>`;

    for (const btn of document.querySelectorAll('[data-view]')) {
        btn.addEventListener('click', () => {
            state.view = btn.dataset.view;
            drawFeed();
        });
    }

    document.getElementById('event-file')?.addEventListener('change', (e) => {
        const files = [...e.target.files];
        e.target.value = '';
        void uploadFiles(files);
    });
    document.getElementById('open-guestbook')?.addEventListener('click', openGuestbookModal);

    drawStage();
    drawTicker();

    // Only poll while the tab is actually being looked at -- an event page left open on a
    // phone in someone's pocket should not spend the evening waking the radio.
    pollTimer = setInterval(() => {
        if (document.visibilityState === 'visible') void refresh();
    }, 15_000);
}

async function refresh() {
    try {
        const previous = state.media.length;
        await loadFeed();
        if (state.media.length !== previous) drawStage();
        drawTicker();
    } catch {
        // A refresh failing is not worth interrupting anyone over; the next tick retries.
    }
}

function drawStage() {
    const stage = document.getElementById('event-stage');
    if (!stage) return;
    clearInterval(slideTimer);

    const ready = state.media.filter((m) => m.status === 'ready');
    if (ready.length === 0) {
        stage.innerHTML = `
            <div class="event-empty">
                <i class="fa-solid fa-camera-retro"></i>
                <p>No photos yet. Be the first.</p>
            </div>`;
        return;
    }

    if (state.view === 'grid') {
        stage.innerHTML = `<div class="event-grid">${ready.map(gridItem).join('')}</div>`;
        for (const el of stage.querySelectorAll('[data-index]')) {
            el.addEventListener('click', () => {
                state.slide = Number(el.dataset.index);
                state.view = 'slideshow';
                drawFeed();
            });
        }
        return;
    }

    if (state.slide >= ready.length) state.slide = 0;
    stage.innerHTML = `
        <div class="event-slideshow">
            <div id="slide-frame" class="event-slide-frame"></div>
            <div class="event-slide-controls">
                <button type="button" id="slide-prev" aria-label="Previous"><i class="fa-solid fa-chevron-left"></i></button>
                <div class="event-slide-dots">${ready.map((_, i) =>
                    `<span class="${i === state.slide ? 'active' : ''}"></span>`).join('')}</div>
                <button type="button" id="slide-next" aria-label="Next"><i class="fa-solid fa-chevron-right"></i></button>
            </div>
        </div>`;

    // True from the moment someone presses play on the current clip until it ends.
    let watching = false;

    const show = () => {
        const item = ready[state.slide];
        const frame = document.getElementById('slide-frame');
        if (!frame || !item) return;
        watching = false;
        frame.innerHTML = item.kind === 'video'
            ? `<video controls playsinline preload="metadata"
                      ${item.thumbnail_url ? `poster="${escapeHtml(item.thumbnail_url)}"` : ''}
                      src="${escapeHtml(item.video_url ?? '')}"></video>`
            : `<img src="${escapeHtml(item.display_url ?? item.thumbnail_url ?? '')}" alt="">`;
        const video = frame.querySelector('video');
        if (video) {
            video.addEventListener('play', () => { watching = true; });
            video.addEventListener('ended', () => { watching = false; step(1); });
        }
        frame.insertAdjacentHTML('beforeend',
            `<p class="event-slide-caption${item.kind === 'video' ? ' is-video' : ''}">${
                escapeHtml(item.guest_name ?? 'An organiser')}</p>`);
        for (const [i, dot] of [...document.querySelectorAll('.event-slide-dots span')].entries()) {
            dot.classList.toggle('active', i === state.slide);
        }
    };

    const step = (delta) => {
        state.slide = (state.slide + delta + ready.length) % ready.length;
        show();
    };

    document.getElementById('slide-prev').addEventListener('click', () => step(-1));
    document.getElementById('slide-next').addEventListener('click', () => step(1));
    show();

    // A clip someone is watching holds the slideshow until it ends -- advancing mid-watch
    // would be maddening. A clip nobody has started is shown for the same time as a photo;
    // holding on every video parked an unattended slideshow on the first clip forever.
    slideTimer = setInterval(() => {
        if (watching) return;
        step(1);
    }, 6000);
}

function gridItem(item, index) {
    // A clip whose poster could not be captured still needs a tile that reads as a video
    // rather than as a broken image.
    const thumb = item.thumbnail_url
        ? `<img src="${escapeHtml(item.thumbnail_url)}" alt="" loading="lazy">`
        : `<div class="event-media-placeholder"><i class="fa-solid fa-film"></i></div>`;

    return `
        <figure class="event-grid-item" data-index="${index}" tabindex="0">
            ${thumb}
            ${item.kind === 'video' ? '<span class="event-media-badge"><i class="fa-solid fa-play"></i></span>' : ''}
            <figcaption>${escapeHtml(item.guest_name ?? '')}</figcaption>
        </figure>`;
}

function drawTicker() {
    const ticker = document.getElementById('event-ticker');
    if (!ticker) return;

    const messages = state.guestbook.filter((g) => g.kind === 'text' && g.body);
    const audio = state.guestbook.filter((g) => g.kind === 'audio');

    if (messages.length === 0 && audio.length === 0) {
        ticker.innerHTML = '';
        return;
    }

    ticker.innerHTML = `
        ${messages.length ? `
            <div class="event-ticker" aria-label="Guest book messages">
                <div class="event-ticker-track">
                    ${[...messages, ...messages].map((m) => `
                        <span class="event-ticker-item">
                            &ldquo;${escapeHtml(m.body)}&rdquo;
                            <em>— ${escapeHtml(m.guest_name ?? 'a guest')}</em>
                        </span>`).join('')}
                </div>
            </div>` : ''}
        ${audio.length ? `
            <div class="event-audio-list">
                <h2>Voice messages</h2>
                ${audio.map((a) => `
                    <div class="event-audio-item">
                        <strong>${escapeHtml(a.guest_name ?? 'A guest')}</strong>
                        <audio controls preload="none" src="${escapeHtml(a.audio_url ?? '')}"></audio>
                    </div>`).join('')}
            </div>` : ''}`;
}

// ------------------------------------------------------------------ Uploading

/**
 * Every upload is type-checked and scanned for malware before anyone sees it -- usually a few
 * seconds, occasionally a minute or two. So a row says "checking" once the bytes are up, rather
 * than claiming the upload is done the moment they arrive, and settles when the check does.
 */
const CHECK_POLL_MS = 4000;
const CHECK_POLL_LIMIT = 45;
/** Bumped on every render, so a check loop started on a page the guest has left stops polling. */
let checkGeneration = 0;

const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

/** 'ready', 'failed', or 'waiting' if the check outlasts the poll. Used for voice messages. */
async function waitForGuestbookCheck(entryId) {
    const generation = checkGeneration;
    for (let attempt = 0; attempt < CHECK_POLL_LIMIT; attempt++) {
        await sleep(CHECK_POLL_MS);
        if (generation !== checkGeneration) return 'waiting';
        try {
            const { guestbook } = await api.getEventUploadStatus(state.slug, state.credential, {
                guestbook: [entryId],
            });
            const status = guestbook?.[entryId];
            if (status === 'ready' || status === 'failed') return status;
        } catch {
            // Try again on the next tick.
        }
    }
    return 'waiting';
}

async function uploadFiles(files) {
    if (files.length === 0) return;
    const progress = document.getElementById('upload-progress');
    progress.classList.remove('hidden');
    progress.innerHTML = '';
    const waiting = [];

    for (const file of files) {
        const row = document.createElement('div');
        row.className = 'event-upload-row';
        row.innerHTML = `<span>${escapeHtml(file.name)}</span><div class="upload-bar"><div></div></div>`;
        progress.appendChild(row);
        const bar = row.querySelector('.upload-bar div');

        try {
            let id;
            if (uploadType(file).startsWith('video/')) id = await uploadVideo(file, bar);
            else if (uploadType(file).startsWith('image/')) id = await uploadPhoto(file, bar);
            else throw new Error('That file type is not supported');
            setRowState(row, 'is-checking', `${file.name} — checking…`);
            waiting.push({ id, row, name: file.name });
        } catch (err) {
            setRowState(row, 'is-error', `${file.name} — ${err.message}`);
        }
    }

    if (waiting.length > 0) await watchUploadChecks(waiting);
    setTimeout(() => progress.classList.add('hidden'), 6000);
}

function setRowState(row, className, text) {
    row.classList.remove('is-checking', 'is-done', 'is-error');
    row.classList.add(className);
    row.querySelector('span').textContent = text;
}

/**
 * Polls until each upload is ready or refused. A refusal is worded the same whatever the reason
 * -- a file that wasn't what it claimed, or one that failed the malware scan -- because an
 * innocent guest with an odd file should not be told they uploaded a virus.
 */
async function watchUploadChecks(items) {
    const generation = checkGeneration;
    const pending = new Map(items.map((item) => [item.id, item]));

    for (let attempt = 0; attempt < CHECK_POLL_LIMIT && pending.size > 0; attempt++) {
        await sleep(CHECK_POLL_MS);
        if (generation !== checkGeneration) return;

        let statuses;
        try {
            ({ media: statuses } = await api.getEventUploadStatus(state.slug, state.credential, {
                media: [...pending.keys()],
            }));
        } catch {
            // A missed poll is not worth telling anyone about; the next one tries again.
            continue;
        }

        let added = 0;
        for (const [id, item] of pending) {
            const status = statuses?.[id];
            if (status === 'ready') {
                setRowState(item.row, 'is-done', `${item.name} — added`);
                pending.delete(id);
                added += 1;
            } else if (status === 'failed') {
                setRowState(item.row, 'is-error', `${item.name} — couldn't be added`);
                showToast(`${item.name} couldn't be added`, 'error');
                pending.delete(id);
            }
        }
        if (added > 0) await refresh();
    }

    for (const item of pending.values()) {
        setRowState(item.row, 'is-checking', `${item.name} — still being checked, it will appear shortly`);
    }
}

async function uploadPhoto(file, bar) {
    const target = await api.createEventUpload(state.slug, state.credential, {
        kind: 'photo',
        contentType: uploadType(file),
        sizeBytes: file.size,
    });
    await api.uploadToS3(target.upload, file, (pct) => { bar.style.width = `${Math.round(pct * 100)}%`; });
    return target.id;
}

async function uploadVideo(file, bar) {
    const poster = await capturePoster(file);
    const target = await api.createEventUpload(state.slug, state.credential, {
        kind: 'video',
        contentType: uploadType(file),
        sizeBytes: file.size,
        ...(poster ? { posterContentType: 'image/webp' } : {}),
    });

    // The poster goes first. When the video's scan comes back clean the server checks for a
    // poster, and makes one if there is none -- sending ours first means it is already there.
    if (poster && target.posterUpload) {
        try {
            await api.uploadToS3(target.posterUpload, poster);
        } catch {
            // A missing poster costs nothing now: the server makes one. Never fail the upload for it.
        }
    }
    await api.uploadToS3(target.upload, file, (pct) => { bar.style.width = `${Math.round(pct * 100)}%`; });
    // No "complete" call. The video goes live when its malware scan comes back clean.
    return target.id;
}

/**
 * A frame is worth keeping if it has some light in it and some variation across it. A
 * black frame scores near zero on both; so does a blown-out white one.
 */
const POSTER_MIN_LUMA = 16;
const POSTER_MAX_LUMA = 246;
const POSTER_MIN_SPREAD = 12;
/** Sampling grid for the check above -- 32x32 is plenty to tell a picture from a blank. */
const PROBE_SIZE = 32;

/** Resolves on `event`, rejects on an error or when the clip stops responding. */
function videoEvent(video, name, timeoutMs = 4000) {
    return new Promise((resolve, reject) => {
        const done = (fn) => (...args) => {
            clearTimeout(timer);
            video.removeEventListener(name, ok);
            video.removeEventListener('error', fail);
            fn(...args);
        };
        const ok = done(resolve);
        const fail = done(() => reject(new Error('cannot decode')));
        const timer = setTimeout(fail, timeoutMs);
        video.addEventListener(name, ok, { once: true });
        video.addEventListener('error', fail, { once: true });
    });
}

/**
 * Resolves when the decoder has actually presented a frame.
 *
 * `seeked` only says the playback position moved; on Android it fires while the decoder
 * still has nothing to hand over, and drawing then leaves the canvas untouched -- which
 * encodes to a perfectly black poster. requestVideoFrameCallback is the only signal that
 * means "there are pixels now"; without it, two animation frames is the pragmatic stand-in.
 */
function presentedFrame(video, timeoutMs = 3000) {
    if (typeof video.requestVideoFrameCallback === 'function') {
        return new Promise((resolve) => {
            const timer = setTimeout(resolve, timeoutMs);
            video.requestVideoFrameCallback(() => { clearTimeout(timer); resolve(); });
        });
    }
    return new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
}

/**
 * Some Android builds never decode anything for a clip that has not been played, however
 * long you wait after a seek. A muted moment of playback forces the first frame out; if
 * autoplay is refused, seeking alone still works everywhere else.
 */
async function primeDecoder(video) {
    try {
        await video.play();
        await presentedFrame(video);
    } catch {
        // Refused, or no frame in time -- the sampling loop copes either way.
    } finally {
        video.pause();
    }
}

function frameStats(ctx) {
    const { data } = ctx.getImageData(0, 0, PROBE_SIZE, PROBE_SIZE);
    let sum = 0;
    let sumSquares = 0;
    const pixels = data.length / 4;

    for (let i = 0; i < data.length; i += 4) {
        const luma = 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
        sum += luma;
        sumSquares += luma * luma;
    }

    const mean = sum / pixels;
    return { mean, spread: Math.sqrt(Math.max(0, sumSquares / pixels - mean * mean)) };
}

/**
 * Grabs a still from the video in the browser and encodes it as WebP.
 *
 * Nothing on the server can open a video file -- there is no ffmpeg anywhere in this
 * design -- so this is the only chance to get a thumbnail. It is best-effort by design:
 * a codec the browser can't decode returns null, the clip uploads anyway, and it simply
 * shows without a poster. The one guarantee worth having is that this never throws.
 *
 * It samples a few points rather than one: a phone starts recording before the sensor has
 * settled, so the opening second is routinely black, and a black poster on a black slide
 * frame reads as a broken tile. The first frame with light and detail in it wins; if none
 * qualifies (a genuinely dark clip), the least-flat one is still better than nothing.
 */
async function capturePoster(file) {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    // 'metadata' is enough to learn the dimensions but not to hand a frame to a canvas.
    video.preload = 'auto';
    video.src = URL.createObjectURL(file);

    try {
        await videoEvent(video, 'loadedmetadata');
        if (!video.videoWidth || !video.videoHeight) return null;
        await primeDecoder(video);

        const duration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 0;
        // Fractions rather than fixed offsets, so a three-second clip and a three-minute one
        // are both sampled across their length. Ordered by how likely each is to be the
        // frame someone would have chosen.
        const times = (duration ? [0.25, 0.5, 0.75, 0.1].map((f) => duration * f) : [1, 0.1])
            .map((t) => Math.min(Math.max(t, 0.05), Math.max(duration - 0.05, 0.05)))
            .filter((t, i, all) => all.indexOf(t) === i);

        const probe = document.createElement('canvas');
        probe.width = PROBE_SIZE;
        probe.height = PROBE_SIZE;
        const probeCtx = probe.getContext('2d', { willReadFrequently: true });

        let best = null;
        let probed = null;
        for (const time of times) {
            video.currentTime = time;
            await videoEvent(video, 'seeked');
            await presentedFrame(video);
            probeCtx.drawImage(video, 0, 0, PROBE_SIZE, PROBE_SIZE);
            probed = time;

            const { mean, spread } = frameStats(probeCtx);
            if (!best || spread > best.spread) best = { time, mean, spread };
            if (mean >= POSTER_MIN_LUMA && mean <= POSTER_MAX_LUMA && spread >= POSTER_MIN_SPREAD) {
                best = { time, mean, spread };
                break;
            }
        }

        if (!best) return null;
        // Compared against the candidate we last asked for, not video.currentTime: a browser
        // snaps to the nearest frame, so the two are rarely equal even when nothing moved,
        // and a needless re-seek would wait for an event that never fires.
        if (best.time !== probed) {
            video.currentTime = best.time;
            await videoEvent(video, 'seeked');
            await presentedFrame(video);
        }

        // Nothing was ever decoded. A poster of pure black is worse than none: the tile
        // reads as broken rather than as a clip waiting to be played.
        if (best.mean < 1 && best.spread < 1) return null;

        const scale = Math.min(1, 1280 / Math.max(video.videoWidth, video.videoHeight));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(video.videoWidth * scale);
        canvas.height = Math.round(video.videoHeight * scale);
        if (!canvas.width || !canvas.height) return null;

        canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
        return await new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', 0.82));
    } catch {
        return null;
    } finally {
        URL.revokeObjectURL(video.src);
    }
}

// ------------------------------------------------------------------ Guest book

function openGuestbookModal() {
    const overlay = document.createElement('div');
    overlay.className = 'modal show';
    overlay.innerHTML = `
        <div class="modal-content">
            <div class="modal-header">
                <h2>Sign the guest book</h2>
                <button type="button" class="modal-close" aria-label="Close">&times;</button>
            </div>
            <div class="modal-body">
                <form id="gb-form">
                    <div class="form-group">
                        <label for="gb-text">Your message</label>
                        <textarea id="gb-text" rows="4" maxlength="2000"
                                  placeholder="A few words for the occasion…"></textarea>
                    </div>
                    <div id="gb-error" class="form-error hidden"></div>
                    <button type="submit" class="btn btn-primary btn-block">Add to the guest book</button>
                </form>
                <div class="event-record">
                    <h3>Or leave a voice message</h3>
                    <button type="button" id="gb-record" class="btn btn-secondary btn-block">
                        <i class="fa-solid fa-microphone"></i> Start recording
                    </button>
                    <p id="gb-record-status" class="form-hint">Up to three minutes.</p>
                </div>
            </div>
        </div>`;

    const close = () => {
        if (recorder?.state === 'recording') recorder.stop();
        recorder = null;
        overlay.remove();
        document.body.classList.remove('modal-open');
    };
    overlay.querySelector('.modal-close').addEventListener('click', close);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
    document.body.appendChild(overlay);
    document.body.classList.add('modal-open');

    const error = overlay.querySelector('#gb-error');
    overlay.querySelector('#gb-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        error.classList.add('hidden');
        const text = overlay.querySelector('#gb-text').value.trim();
        if (!text) {
            error.textContent = 'Please write something first.';
            error.classList.remove('hidden');
            return;
        }
        try {
            await api.postEventGuestbook(state.slug, state.credential, { kind: 'text', body: text });
            close();
            showToast('Thank you — added to the guest book', 'success');
            await refresh();
        } catch (err) {
            error.textContent = err.message;
            error.classList.remove('hidden');
        }
    });

    overlay.querySelector('#gb-record').addEventListener('click', () => toggleRecording(overlay, close));
}

/**
 * A clip is stored and served back exactly as recorded, so the API only accepts the
 * containers every browser plays natively. Left to itself MediaRecorder picks whatever the
 * device prefers -- and several Android browsers pick something outside that list, or
 * report an empty mimeType -- so ask for a known-good format up front rather than finding
 * out after the recording that the upload will be refused.
 */
const RECORDING_FORMATS = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4;codecs=mp4a.40.2',
    'audio/mp4',
    'audio/ogg;codecs=opus',
    'audio/ogg',
];

/** The base types the API allows, i.e. RECORDING_FORMATS without their codec parameters. */
const UPLOADABLE_TYPES = ['audio/webm', 'audio/mp4', 'audio/ogg', 'audio/mpeg'];

const baseType = (mime) => mime.split(';')[0].trim().toLowerCase();

function pickRecordingFormat() {
    if (typeof MediaRecorder === 'undefined') return null;
    // isTypeSupported is missing on some older Android WebViews; there, ask for the first
    // format and let the constructor tell us whether it is really available.
    if (typeof MediaRecorder.isTypeSupported !== 'function') return RECORDING_FORMATS[0];
    return RECORDING_FORMATS.find((type) => MediaRecorder.isTypeSupported(type)) ?? null;
}

async function toggleRecording(overlay, close) {
    const button = overlay.querySelector('#gb-record');
    const status = overlay.querySelector('#gb-record-status');

    if (recorder?.state === 'recording') {
        recorder.stop();
        return;
    }

    const format = pickRecordingFormat();
    if (!format) {
        status.textContent = 'This browser cannot record audio. You can still leave a written message.';
        return;
    }

    let stream;
    try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
        status.textContent = 'We could not use your microphone. Check the browser permission.';
        return;
    }

    const chunks = [];
    try {
        recorder = new MediaRecorder(stream, { mimeType: format });
    } catch {
        recorder = new MediaRecorder(stream);
    }
    const startedAt = Date.now();

    recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
    recorder.onstop = async () => {
        for (const track of stream.getTracks()) track.stop();
        const seconds = Math.max(1, Math.round((Date.now() - startedAt) / 1000));
        // The clip is stored and served as-is, so its type has to travel with it. Some
        // Android browsers leave mimeType empty even after recording, so fall back to the
        // format we asked for.
        const contentType = baseType(recorder.mimeType || format);
        if (!UPLOADABLE_TYPES.includes(contentType)) {
            button.disabled = false;
            button.innerHTML = '<i class="fa-solid fa-microphone"></i> Start recording';
            status.textContent = `This browser recorded in a format we cannot accept (${contentType || 'unknown'}). Please leave a written message.`;
            return;
        }
        const blob = new Blob(chunks, { type: contentType });
        button.disabled = true;
        status.textContent = 'Uploading…';

        try {
            const created = await api.postEventGuestbook(state.slug, state.credential, {
                kind: 'audio',
                contentType,
                durationSeconds: seconds,
            });
            await api.uploadToS3(created.upload, blob);

            // A recording is checked like any other upload before it is played to anyone.
            status.textContent = 'Checking your message…';
            const outcome = await waitForGuestbookCheck(created.id);
            if (outcome === 'failed') {
                button.disabled = false;
                button.innerHTML = '<i class="fa-solid fa-microphone"></i> Start recording';
                status.textContent = "Sorry, your message couldn't be added. You can still leave a written one.";
                return;
            }
            close();
            showToast(outcome === 'ready'
                ? 'Thank you — your message was added'
                : 'Thank you — your message will appear once it has been checked', 'success');
            await refresh();
        } catch (err) {
            button.disabled = false;
            button.innerHTML = '<i class="fa-solid fa-microphone"></i> Start recording';
            status.textContent = err.message;
        }
    };

    recorder.start();
    button.innerHTML = '<i class="fa-solid fa-stop"></i> Stop and send';
    status.textContent = 'Recording…';
    setTimeout(() => { if (recorder?.state === 'recording') recorder.stop(); }, 180_000);
}
